import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { Link } from 'react-router-dom';

const MajorProjectsPage = () => {
  const projects = [
    { name: 'Commercial Office Complex', location: 'Bucks County, PA', value: '$45 Million', type: 'Commercial Construction', description: 'Multi-story office building with state-of-the-art facilities and sustainable design features bonded by The Stoll Agency.', icon: 'ri-building-4-line' },
    { name: 'Highway Infrastructure', location: 'Pennsylvania Turnpike', value: '$78 Million', type: 'Civil Engineering', description: 'Major highway expansion and improvement project including bridges and interchanges across Pennsylvania.', icon: 'ri-road-map-line' },
    { name: 'Healthcare Facility', location: 'Montgomery County, PA', value: '$62 Million', type: 'Healthcare Construction', description: 'Modern medical center with advanced surgical suites and patient care facilities in Montgomery County.', icon: 'ri-hospital-line' },
    { name: 'Educational Campus', location: 'State College, PA', value: '$38 Million', type: 'Educational Construction', description: 'University research and academic building with cutting-edge laboratories and classrooms.', icon: 'ri-school-line' },
    { name: 'Industrial Manufacturing Plant', location: 'Harrisburg, PA', value: '$95 Million', type: 'Industrial Construction', description: 'Large-scale manufacturing facility with automated systems and logistics infrastructure.', icon: 'ri-factory-line' },
    { name: 'Residential Development', location: 'Chester County, PA', value: '$52 Million', type: 'Residential Construction', description: 'Mixed-use residential community with retail spaces and recreational amenities.', icon: 'ri-community-line' },
  ];

  const capabilities = [
    { title: 'Large Project Bonding', description: 'Capacity to bond Pennsylvania projects exceeding $100 million', icon: 'ri-money-dollar-circle-line' },
    { title: 'Multi-State Coverage', description: 'Experience with projects across multiple states', icon: 'ri-map-pin-line' },
    { title: 'Complex Risk Management', description: 'Sophisticated insurance solutions for major PA projects', icon: 'ri-shield-star-line' },
    { title: 'Fast Processing', description: 'Quick turnaround on large bond requests in Bucks & Montgomery County', icon: 'ri-speed-line' },
  ];

  return (
    <div className="min-h-screen bg-slate-900">
      <Navbar />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950">
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-green-400 text-sm font-semibold tracking-widest uppercase mb-4">Pennsylvania Construction Bonding</p>
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">Major Projects</h1>
          <p className="text-xl text-slate-300 max-w-3xl mx-auto">
            Bonding and insuring Pennsylvania's largest construction projects — from Bucks County to Pittsburgh
          </p>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-20 bg-slate-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            Trusted Partner for Major Construction Projects in Pennsylvania
          </h2>
          <p className="text-lg text-slate-300 leading-relaxed">
            With decades of experience in the construction industry, The Stoll Agency has
            successfully bonded and insured some of Pennsylvania's most significant construction
            projects. Our expertise in large-scale project management and risk assessment makes us
            the preferred choice for major contractors in <strong className="text-white">Bucks County</strong>, <strong className="text-white">Montgomery County</strong>, and throughout the Commonwealth.
          </p>
        </div>
      </section>

      {/* Projects Grid */}
      <section className="py-20 bg-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">Featured Projects</h2>
            <div className="w-24 h-1 bg-green-500 mx-auto"></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {projects.map((project, index) => (
              <div key={index} className="bg-slate-900 border border-slate-700 rounded-2xl overflow-hidden hover:border-green-500/50 hover:shadow-xl hover:shadow-green-900/20 transition-all duration-300 hover:-translate-y-1">
                <div className="bg-gradient-to-br from-green-700 to-green-900 p-8 text-center">
                  <div className="w-20 h-20 bg-white/10 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className={`${project.icon} text-4xl text-white`}></i>
                  </div>
                  <h3 className="text-xl font-bold text-white mb-2">{project.name}</h3>
                  <p className="text-white/80 text-sm">{project.location}</p>
                </div>
                <div className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-sm font-semibold text-slate-500">Project Value</span>
                    <span className="text-xl font-bold text-green-400">{project.value}</span>
                  </div>
                  <div className="mb-4">
                    <span className="inline-block px-3 py-1 bg-green-500/10 text-green-400 border border-green-500/20 rounded-full text-xs font-semibold">
                      {project.type}
                    </span>
                  </div>
                  <p className="text-slate-400 text-sm leading-relaxed">{project.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Capabilities */}
      <section className="py-20 bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">Our Capabilities</h2>
            <div className="w-24 h-1 bg-green-500 mx-auto"></div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {capabilities.map((capability, index) => (
              <div key={index} className="text-center bg-slate-800 border border-slate-700 rounded-2xl p-8 hover:border-green-500/50 transition-all">
                <div className="w-16 h-16 bg-green-500/10 border border-green-500/20 rounded-xl flex items-center justify-center mx-auto mb-6">
                  <i className={`${capability.icon} text-3xl text-green-400`}></i>
                </div>
                <h3 className="text-xl font-bold text-white mb-3">{capability.title}</h3>
                <p className="text-slate-400 text-sm">{capability.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Statistics */}
      <section className="py-20 bg-gradient-to-br from-green-700 to-green-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 text-center">
            {[
              { value: '$500M+', label: 'Total Project Value' },
              { value: '100+', label: 'Major Projects' },
              { value: '50+', label: 'Years Experience' },
              { value: '100%', label: 'Client Satisfaction' },
            ].map((stat, i) => (
              <div key={i}>
                <div className="text-5xl font-bold text-white mb-2">{stat.value}</div>
                <div className="text-white/80 font-medium">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-slate-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">Planning a Major Project in Pennsylvania?</h2>
          <p className="text-xl text-slate-300 mb-10">
            Let us help you secure the construction bonding and insurance coverage you need in Bucks County, Montgomery County, or anywhere in PA
          </p>
          <Link
            to="/contact"
            className="inline-block px-10 py-4 bg-green-500 text-white rounded-full font-semibold text-lg hover:bg-green-400 transition-all shadow-lg whitespace-nowrap"
          >
            Contact Us Today
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default MajorProjectsPage;
